import * as React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClientService from '../../../services/client.service';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export default function List() {
  const [clients, setClients] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchClients = React.useCallback(
    async (nextPage = page, nextRowsPerPage = rowsPerPage, nextSearch = search) => {
      setLoading(true);
      try {
        const response = await ClientService.list({
          page: nextPage + 1,
          limit: nextRowsPerPage,
          search: nextSearch,
        });

        const payload = response?.data || response;
        setClients(payload?.clients || []);
        setTotal(payload?.pagination?.total || 0);
      } catch (error) {
        setClients([]);
        setTotal(0);
        setSnackbar({
          open: true,
          message: error.message || 'Erreur lors du chargement des clients',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, search]
  );

  React.useEffect(() => {
    fetchClients(0, rowsPerPage, '');
  }, []);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearch = async () => {
    setPage(0);
    await fetchClients(0, rowsPerPage, search);
  };

  const handleReset = async () => {
    setSearch('');
    setPage(0);
    await fetchClients(0, rowsPerPage, '');
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    await fetchClients(newPage, rowsPerPage, search);
  };

  const handleChangeRowsPerPage = async (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
    await fetchClients(0, nextRowsPerPage, search);
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper sx={{ width: '100%', mb: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontSize: '20px', fontWeight: 'bold' }}>
            Clients
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={search}
              onChange={handleSearchChange}
              placeholder="Rechercher un client..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#e0e0e0' },
                },
              }}
            />
            <Button variant="contained" onClick={handleSearch}>
              Rechercher
            </Button>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
              Réinitialiser
            </Button>
          </Box>
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell>Nom complet</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Pays</TableCell>
                <TableCell>Date création</TableCell>
                <TableCell>Date mise à jour</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <TableRow hover key={client.id}>
                  <TableCell>{client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'N/A'}</TableCell>
                  <TableCell>{client.email || 'N/A'}</TableCell>
                  <TableCell>{client.phone || 'N/A'}</TableCell>
                  <TableCell>{client.country || 'N/A'}</TableCell>
                  <TableCell>{formatDate(client.createdAt)}</TableCell>
                  <TableCell>{formatDate(client.updatedAt)}</TableCell>
                </TableRow>
              ))}
              {!loading && clients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
